/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, MapPin, Clock, ChevronLeft, ChevronRight, Users, Star, Filter, ExternalLink, CheckCircle2, Ticket
} from 'lucide-react';
import { Button } from '../../components/ui/button';

import Img from '../../components/Img';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const categoryColors = {
  networking: 'bg-blue-500',
  gala: 'bg-purple-500',
  workshop: 'bg-green-500',
  social: 'bg-orange-500',
  general: 'bg-slate-500',
  member: 'bg-[#E63946]',
};

const PortalEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('calendar');
  const [filter, setFilter] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    fetchEvents();
    fetchMyRegistrations();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`, { withCredentials: true });
      setEvents(res.data);
    } catch (err) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  };

  const fetchMyRegistrations = async () => {
    try {
      const res = await axios.get(`${API}/portal/my-registrations`, { withCredentials: true });
      setMyRegistrations(res.data);
    } catch (err) {
      // error handled silently
    }
  };

  const isRegistered = (eventId) => myRegistrations.some(r => r.event_id === eventId);

  const handleRegister = async (eventId) => {
    setRegistering(true);
    try {
      await axios.post(`${API}/events/${eventId}/register`, { event_id: eventId }, { withCredentials: true });
      toast.success('Registration confirmed!');
      fetchMyRegistrations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to register');
    } finally {
      setRegistering(false);
    }
  };

  const handleCancelRegistration = async (eventId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/register`, { withCredentials: true });
      toast.success('Registration cancelled');
      fetchMyRegistrations();
    } catch (err) {
      toast.error('Failed to cancel registration');
    }
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  };

  const filteredEvents = filter === 'all' ? events :
    filter === 'chamber' ? events.filter(e => !e.is_member_event) :
    events.filter(e => e.is_member_event);

  const upcomingEvents = filteredEvents
    .filter(e => e.date >= new Date().toISOString().split('T')[0])
    .sort((a, b) => a.date.localeCompare(b.date));

  if (loading) {
    return (
      <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="portal-events">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-[#002249] rounded-sm flex items-center justify-center">
            <Calendar className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#002249]">Events Calendar</h1>
        </div>
        <p className="text-slate-600 mt-2">Stay connected with chamber and member events.</p>
      </motion.div>

      {/* Controls */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6"
      >
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setView('calendar')}
            className={`px-4 py-2 rounded-sm text-sm font-medium transition-all ${view === 'calendar' ? 'bg-[#002249] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
            data-testid="view-calendar-btn"
          >
            Calendar
          </button>
          <button
            onClick={() => setView('list')}
            className={`px-4 py-2 rounded-sm text-sm font-medium transition-all ${view === 'list' ? 'bg-[#002249] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
            data-testid="view-list-btn"
          >
            List
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-slate-500" />
          {['all', 'chamber', 'member'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-all ${filter === f ? 'bg-[#E63946] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? 'All Events' : f === 'chamber' ? 'Chamber Events' : 'Member Events'}
            </button>
          ))}
        </div>
      </motion.div>

      {view === 'calendar' ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white border border-slate-200 rounded-sm overflow-hidden"
          data-testid="calendar-view"
        >
          {/* Calendar Header */}
          <div className="flex items-center justify-between p-4 bg-[#002249] text-white">
            <button onClick={prevMonth} className="p-2 hover:bg-white/10 rounded-sm" data-testid="prev-month-btn">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-bold">{MONTHS[month]} {year}</h2>
            <button onClick={nextMonth} className="p-2 hover:bg-white/10 rounded-sm" data-testid="next-month-btn">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
            {DAYS.map(day => (
              <div key={day} className="py-2 text-center text-xs font-semibold text-slate-500 uppercase">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7">
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} className="min-h-[80px] md:min-h-[100px] border-b border-r border-slate-100 bg-slate-50/50" />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dayEvents = getEventsForDay(day);
              const today = new Date();
              const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

              return (
                <div
                  key={day}
                  className={`min-h-[80px] md:min-h-[100px] border-b border-r border-slate-100 p-1 ${isToday ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                  data-testid={`calendar-day-${day}`}
                >
                  <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-medium rounded-full ${isToday ? 'bg-[#002249] text-white' : 'text-slate-700'}`}>
                    {day}
                  </span>
                  <div className="mt-1 space-y-0.5">
                    {dayEvents.slice(0, 2).map((event, idx) => (
                      <button
                        key={event.id || idx}
                        onClick={() => setSelectedEvent(event)}
                        className={`w-full text-left text-[10px] md:text-xs px-1 py-0.5 rounded-sm text-white truncate ${categoryColors[event.category] || categoryColors.general} ${event.is_member_event ? '!bg-[#E63946]' : ''}`}
                      >
                        {event.title}
                      </button>
                    ))}
                    {dayEvents.length > 2 && (
                      <span className="text-[10px] text-slate-500 px-1">+{dayEvents.length - 2} more</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      ) : (
        /* List View */
        <div className="space-y-4" data-testid="list-view">
          {upcomingEvents.length > 0 ? upcomingEvents.map((event, index) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * index }}
              className="bg-white border border-slate-200 rounded-sm p-5 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedEvent(event)}
              data-testid={`event-card-${index}`}
            >
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-[#002249] rounded-sm flex flex-col items-center justify-center flex-shrink-0 text-white">
                  <span className="text-xl font-bold">{event.date?.split('-')[2]}</span>
                  <span className="text-xs text-white/70">{MONTHS[parseInt(event.date?.split('-')[1]) - 1]?.slice(0, 3)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className="font-semibold text-[#002249]">{event.title}</h3>
                    {event.is_member_event && (
                      <span className="bg-[#E63946]/10 text-[#E63946] text-xs font-medium px-2 py-0.5 rounded-sm">Member Event</span>
                    )}
                    {isRegistered(event.id) && (
                      <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-0.5 rounded-sm flex items-center"><CheckCircle2 className="w-3 h-3 mr-1" />Registered</span>
                    )}
                    {event.is_featured && (
                      <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                    )}
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-2">{event.description}</p>
                  <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center"><Clock className="w-3.5 h-3.5 mr-1" />{event.time}</span>
                    <span className="flex items-center"><MapPin className="w-3.5 h-3.5 mr-1" />{event.location}</span>
                    <span className={`px-2 py-0.5 rounded-sm text-white text-xs ${categoryColors[event.category] || categoryColors.general}`}>{event.category}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )) : (
            <div className="text-center py-16 text-slate-500">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">No upcoming events</p>
              <p className="text-sm mt-1">Check back soon for new events!</p>
            </div>
          )}
        </div>
      )}

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedEvent(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-sm max-w-lg w-full max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
            data-testid="event-detail-modal"
          >
            {selectedEvent.image_url && (
              <Img variant="event" src={selectedEvent.image_url} alt={selectedEvent.title} className="w-full h-48 object-cover" />
            )}
            <div className="p-6">
              <div className="flex items-center space-x-2 mb-2">
                {selectedEvent.is_member_event && (
                  <span className="bg-[#E63946]/10 text-[#E63946] text-xs font-medium px-2 py-0.5 rounded-sm">Member Event</span>
                )}
                <span className={`px-2 py-0.5 rounded-sm text-white text-xs ${categoryColors[selectedEvent.category] || categoryColors.general}`}>
                  {selectedEvent.category}
                </span>
              </div>
              <h2 className="text-xl font-bold text-[#002249] mb-3">{selectedEvent.title}</h2>
              <p className="text-slate-600 text-sm mb-4">{selectedEvent.description}</p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center text-sm text-slate-700">
                  <Calendar className="w-4 h-4 mr-3 text-[#002249]" />
                  {new Date(selectedEvent.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
                <div className="flex items-center text-sm text-slate-700">
                  <Clock className="w-4 h-4 mr-3 text-[#002249]" />
                  {selectedEvent.time}
                </div>
                <div className="flex items-center text-sm text-slate-700">
                  <MapPin className="w-4 h-4 mr-3 text-[#002249]" />
                  {selectedEvent.location}
                </div>
              </div>

              {selectedEvent.registration_link && (
                <a href={selectedEvent.registration_link} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-[#002249] hover:bg-[#001630] rounded-sm" data-testid="register-event-btn">
                    Register for Event <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              )}
              {!selectedEvent.registration_link && (
                isRegistered(selectedEvent.id) ? (
                  <div>
                    <div className="flex items-center justify-center text-green-600 text-sm font-medium mb-2">
                      <CheckCircle2 className="w-4 h-4 mr-2" /> You're registered!
                    </div>
                    <Button variant="outline" className="w-full rounded-sm text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleCancelRegistration(selectedEvent.id)} data-testid="cancel-registration-btn">
                      Cancel Registration
                    </Button>
                  </div>
                ) : (
                  <Button
                    className="w-full bg-[#E63946] hover:bg-[#d32f3f] rounded-sm"
                    onClick={() => handleRegister(selectedEvent.id)}
                    disabled={registering}
                    data-testid="register-event-btn"
                  >
                    <Ticket className="w-4 h-4 mr-2" />
                    {registering ? 'Registering...' : 'Register for This Event'}
                  </Button>
                )
              )}
              <Button variant="outline" className="w-full mt-2 rounded-sm" onClick={() => setSelectedEvent(null)} data-testid="close-event-modal-btn">
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PortalEvents;
